import type { Difficulty } from "./match.ts";

/**
 * Simpatias (doc 02 §14 e "Parâmetros propostos"; doc 07 J4–J6): powerups acumuláveis por
 * jogador, calculados no servidor. Curva H(n; M, h) = M·n/(n + h), sempre com teto; lineares só
 * Vida, Patuá e Fôlego máximos. As 4 da J5 (Mel de Jataí, Pena de Arara, Couro de Jacaré e
 * Chocalho de Cascavel) não têm fórmula no doc: os números delas são premissa nossa, no mesmo
 * molde das vizinhas de raridade.
 */
export type SimpatiaRarity = "comum" | "rara" | "lendaria";

export const H = (n: number, max: number, half: number): number => (n <= 0 ? 0 : (max * n) / (n + half));

export interface SimpatiaDef {
  id: string;
  name: string;
  rarity: SimpatiaRarity;
  /** Pilhas máximas (a Simpatia sai do sorteio de quem chegou nelas). */
  cap?: number;
  /** Texto do efeito com `n` pilhas (HUD). */
  describe(n: number): string;
}

const pct = (v: number) => `${Math.round(v * 100)} %`;

export const SIMPATIAS: readonly SimpatiaDef[] = [
  { id: "fita-chita", name: "Coração Robusto", rarity: "comum", describe: (n) => `+${12 * n} de Vida máxima` },
  { id: "arruda", name: "Ramo de Sálvia", rarity: "comum", describe: (n) => `+${15 * n} de Égide máxima` },
  { id: "pimenta", name: "Força do Touro", rarity: "comum", describe: (n) => `+${pct(H(n, 1, 8))} de força` },
  { id: "guarana", name: "Pés Ligeiros", rarity: "comum", describe: (n) => `+${pct(H(n, 0.6, 5))} de velocidade` },
  { id: "cafe", name: "Mão Ágil", rarity: "comum", describe: (n) => `+${pct(H(n, 0.8, 4))} de velocidade de ataque` },
  { id: "rapadura", name: "Hidromel", rarity: "comum", describe: (n) => `+${10 * n} de Fôlego máximo, +${pct(H(n, 1, 4))} de recuperação` },
  { id: "cuscuz", name: "Pão de Viagem", rarity: "comum", describe: (n) => `−${pct(H(n, 0.6, 4))} de perda de Fome` },
  { id: "bateia", name: "Bateia do Garimpeiro", rarity: "comum", describe: (n) => `+${pct(H(n, 1.5, 4))} de rendimento de coleta` },
  { id: "pena-arara", name: "Pena de Grifo", rarity: "comum", describe: (n) => `+${pct(H(n, 0.8, 4))} de dano de flecha` },
  { id: "chapeu-couro", name: "Pele de Urso", rarity: "rara", describe: (n) => `+${Math.round(H(n, 60, 5))} de defesa` },
  { id: "ferradura", name: "Ferradura", rarity: "rara", describe: (n) => `+${pct(H(n, 1, 5))} de sorte nos baús` },
  { id: "figa", name: "Trevo de Quatro Folhas", rarity: "rara", describe: (n) => `+${pct(H(n, 0.5, 6))} de chance de crítico` },
  { id: "pena-gaviao", name: "Pena de Gavião", rarity: "rara", cap: 4, describe: (n) => `${Math.min(4, n)} salto${n > 1 ? "s" : ""} no ar` },
  { id: "sal-grosso", name: "Sal Abençoado", rarity: "rara", describe: (n) => `+${pct(H(n, 1, 5))} de dano contra monstros` },
  { id: "unha-onca", name: "Presa de Vampiro", rarity: "rara", describe: (n) => `${pct(H(n, 0.3, 6))} do golpe vira Vida (até 15)` },
  { id: "mel-jatai", name: "Mel Silvestre", rarity: "rara", describe: (n) => `+${H(n, 1.5, 4).toFixed(1)} de Vida por segundo` },
  { id: "couro-jacare", name: "Pele de Basilisco", rarity: "rara", describe: (n) => `−${pct(H(n, 0.5, 4))} de dano com menos de 30 % de Vida` },
  { id: "trovoada", name: "Runa do Trovão", rarity: "lendaria", describe: (n) => `${pct(H(n, 0.35, 4))} de chance de raio (×${(1.2 + H(n, 1, 4)).toFixed(2)})` },
  { id: "bodoque", name: "Funda Encantada", rarity: "lendaria", describe: (n) => `até +${pct(H(n, 1.2, 3))} de dano correndo` },
  { id: "chocalho", name: "Anel de Gelo", rarity: "lendaria", describe: (n) => `quem te acerta fica ${pct(H(n, 0.5, 3))} mais lento por 2 s` },
];

export const SIMPATIA_BY_ID = new Map(SIMPATIAS.map((s) => [s.id, s]));

export const SIMPATIA_RARITY_NAMES: Record<SimpatiaRarity, string> = { comum: "Comum", rara: "Rara", lendaria: "Lendária" };

/** Efeitos somados de todas as Simpatias de um jogador. Neutros quando não há nenhuma. */
export interface SimpatiaEffects {
  hpMaxBonus: number;
  patuaMaxBonus: number;
  /** Corpo a corpo (Pimenta-Malagueta). */
  meleeMul: number;
  /** Flechas (Pena de Arara). */
  arrowMul: number;
  speedMul: number;
  /** Divide o ciclo do golpe (Café Coado). */
  attackSpeedMul: number;
  staminaMaxBonus: number;
  staminaRegenMul: number;
  hungerMul: number;
  harvestMul: number;
  defense: number;
  /** Ferradura: quantidades e chances raras dos baús (`LootLuck`). */
  lootQtyMul: number;
  lootRareBoost: number;
  critBonus: number;
  airJumps: number;
  vsHauntMul: number;
  lifesteal: number;
  hpRegen: number;
  /** Fração do dano tirada abaixo de 30 % de Vida (Couro de Jacaré). */
  lowHpReduction: number;
  lightningChance: number;
  lightningMul: number;
  /** Bônus máximo (a 9 m/s) do Bodoque Encantado. */
  momentumMax: number;
  /** Lentidão (fração tirada) em quem acerta o jogador (Chocalho de Cascavel). */
  rattleSlow: number;
  // Classes (Fase 8, F64; `withClass`): neutros sem classe.
  /** Família de arma de afinidade ("" sem classe) e o multiplicador do dano com ela. */
  affinity: string;
  affinityMul: number;
  /** Baluarte: fração do dano de frente tirada andando sem atacar (Escudo Erguido). */
  frontGuard: number;
  /** Lanceiro: dano a mais no golpe depois de correr (Ímpeto). */
  impetus: number;
  /** Lanceiro: postura a mais em alvos Presos ou Marcados (Ímpeto, F77). */
  impetusPosture: number;
  /** Arcanista: fração do dano do Eco (Ressonância). */
  resonance: number;
  /** Patrulheiro: dano a mais nas flechas de longe (Olho de Falcão). */
  hawkEye: number;
  /** Lanceiro: velocidade da Investida (m/s; 0 sem ela), que vai no corpo previsto (F66). */
  dashSpeed: number;
}

/** Teto do roubo de Vida por golpe (doc 02, Unha de Onça). */
export const LIFESTEAL_CAP = 15;
/** Trovoada de Bolso: raio de 3 m, até 4 alvos, recarga interna de 0,5 s. */
export const LIGHTNING = { radius: 3, targets: 4, cooldown: 0.5 } as const;
/** Bodoque Encantado: bônus cheio a 9 m/s. */
export const MOMENTUM_SPEED = 9;
/** Couro de Jacaré vale abaixo desta fração da Vida. */
export const LOW_HP_FRACTION = 0.3;
/** Chocalho de Cascavel: segundos de lentidão. */
export const RATTLE_SECONDS = 2;

export function simpatiaEffects(count: (id: string) => number): SimpatiaEffects {
  const n = (id: string) => Math.max(0, count(id) | 0);
  return {
    hpMaxBonus: 12 * n("fita-chita"),
    patuaMaxBonus: 15 * n("arruda"),
    meleeMul: 1 + H(n("pimenta"), 1, 8),
    arrowMul: 1 + H(n("pena-arara"), 0.8, 4),
    speedMul: 1 + H(n("guarana"), 0.6, 5),
    attackSpeedMul: 1 + H(n("cafe"), 0.8, 4),
    staminaMaxBonus: 10 * n("rapadura"),
    staminaRegenMul: 1 + H(n("rapadura"), 1, 4),
    hungerMul: 1 - H(n("cuscuz"), 0.6, 4),
    harvestMul: 1 + H(n("bateia"), 1.5, 4),
    defense: H(n("chapeu-couro"), 60, 5),
    lootQtyMul: 1 + H(n("ferradura"), 1, 5),
    lootRareBoost: Math.min(0.5, 0.1 * n("ferradura")),
    critBonus: H(n("figa"), 0.5, 6),
    airJumps: Math.min(4, n("pena-gaviao")),
    vsHauntMul: 1 + H(n("sal-grosso"), 1, 5),
    lifesteal: H(n("unha-onca"), 0.3, 6),
    hpRegen: H(n("mel-jatai"), 1.5, 4),
    lowHpReduction: H(n("couro-jacare"), 0.5, 4),
    lightningChance: H(n("trovoada"), 0.35, 4),
    lightningMul: 1.2 + H(n("trovoada"), 1, 4),
    momentumMax: H(n("bodoque"), 1.2, 3),
    rattleSlow: H(n("chocalho"), 0.5, 3),
    affinity: "",
    affinityMul: 1,
    frontGuard: 0,
    impetus: 0,
    impetusPosture: 0,
    resonance: 0,
    hawkEye: 0,
    dashSpeed: 0,
  };
}

export const NO_SIMPATIAS: SimpatiaEffects = simpatiaEffects(() => 0);

/** Ciclo do golpe com o Café Coado (o servidor e a porta local do cliente usam o mesmo). */
export const swingCycle = (cycle: number, effects: SimpatiaEffects): number => cycle / effects.attackSpeedMul;

/** Multiplicador do Bodoque Encantado para quem bate a `speed` m/s. */
export const momentumMul = (effects: SimpatiaEffects, speed: number): number => 1 + effects.momentumMax * Math.min(1, Math.max(0, speed) / MOMENTUM_SPEED);

// --- Baús de Simpatia (doc 03, catálogo) ---

export type SimpatiaChestKind = "achado" | "simpatia" | "caprichado" | "encantado";

export interface SimpatiaChestDef {
  kind: SimpatiaChestKind;
  item: string;
  name: string;
  base: number;
  weights: Record<SimpatiaRarity, number>;
  /** Peso de geração no mundo. */
  spawn: number;
}

export const SIMPATIA_CHESTS: Record<SimpatiaChestKind, SimpatiaChestDef> = {
  achado: { kind: "achado", item: "bau-achado", name: "Baú Achado", base: 0, weights: { comum: 0.88, rara: 0.11, lendaria: 0.01 }, spawn: 0.08 },
  simpatia: { kind: "simpatia", item: "bau-simpatia", name: "Baú de Bênção", base: 30, weights: { comum: 0.78, rara: 0.2, lendaria: 0.02 }, spawn: 0.6 },
  caprichado: { kind: "caprichado", item: "bau-caprichado", name: "Baú Ornamentado", base: 90, weights: { comum: 0, rara: 0.82, lendaria: 0.18 }, spawn: 0.22 },
  encantado: { kind: "encantado", item: "bau-encantado", name: "Baú Encantado", base: 220, weights: { comum: 0, rara: 0, lendaria: 1 }, spawn: 0.1 },
};

const SIMPATIA_CHEST_BY_ITEM = new Map(Object.values(SIMPATIA_CHESTS).map((c) => [c.item, c]));
export const simpatiaChestOf = (itemId: string): SimpatiaChestDef | undefined => SIMPATIA_CHEST_BY_ITEM.get(itemId);

/** Alta do preço por dia (doc 03): r = 0,10 / 0,15 / 0,20. */
const PRICE_RISE: Record<Difficulty, number> = { tranquilo: 0.1, arretado: 0.15, sinistro: 0.2 };

/** Preço = base × (1 + max(0, dia − 2) × r), arredondado para múltiplo de 5 (doc 03). */
export function simpatiaPrice(base: number, day: number, difficulty: Difficulty): number {
  if (base <= 0) return 0;
  return Math.round((base * (1 + Math.max(0, day - 2) * PRICE_RISE[difficulty])) / 5) * 5;
}

/** Distância para abrir um Baú de Simpatia (doc 03). */
export const SIMPATIA_CHEST_REACH = 3;

/**
 * Sorteia a Simpatia de um baú: a raridade pelos pesos e, dentro dela, uma uniforme entre as que
 * o comprador ainda pode ganhar (as que bateram no teto saem do sorteio). `rand` devolve [0, 1).
 */
export function rollSimpatia(kind: SimpatiaChestKind, rand: () => number, owned: (id: string) => number): SimpatiaDef {
  return rollSimpatiaBy(SIMPATIA_CHESTS[kind].weights, rand, owned);
}

/** Igual a `rollSimpatia`, com os pesos de raridade dados (chefes e Desafios). */
export function rollSimpatiaBy(weights: Record<SimpatiaRarity, number>, rand: () => number, owned: (id: string) => number): SimpatiaDef {
  const order: SimpatiaRarity[] = ["comum", "rara", "lendaria"];
  let u = rand();
  let rarity: SimpatiaRarity = order.find((r) => weights[r] > 0) ?? "comum";
  for (const r of order) {
    if (u < weights[r]) {
      rarity = r;
      break;
    }
    u -= weights[r];
  }
  const pool = SIMPATIAS.filter((s) => s.rarity === rarity && (s.cap === undefined || owned(s.id) < s.cap));
  return pool[Math.floor(rand() * pool.length)] ?? (SIMPATIAS[0] as SimpatiaDef);
}
